import { useEffect, useState } from "react";
import { ModelTranslationStatus } from "../../../../../responses/linkedModel";
import { LinkedModelOriginEditor } from "./linkedModelOriginEditor";
import { LinkedModelThumbnail } from "./linkedModelThumbnail";
import { LinkedModelVisibilityControl } from "./linkedModelVisibilityControl";
import { RemoveLinkedModel } from "./removeLinkedModel";
import { LinkedModelUpdatedEventPayload, LinkedModelView } from "../../../eventBus/linkedModelsEventPayloads";
import eventBus, { IApplicationEvent } from "../../../eventBus/eventDispatcher";
import "./linkedModelCard.css";

type Props = {
    model: LinkedModelView;
    controlsEnabled: boolean;
    onSelect?: (model: LinkedModelView) => void | Promise<void>;
};

export const LinkedModelCard = ({ model, controlsEnabled, onSelect }: Props) => {
    const [status, setStatus] = useState<ModelTranslationStatus>(model.status);
    const [translationProgressPercentage, setTranslationProgressPercentage] = useState<number>(
        model.translationProgressPercentage,
    );

    useEffect(() => {
        const onModelUpdated = (eventData: IApplicationEvent<LinkedModelUpdatedEventPayload>) => {
            const updatedData = eventData.payload;

            if (updatedData.id !== model.id) {
                return;
            }

            setStatus(updatedData.status);
            setTranslationProgressPercentage(updatedData.translationProgressPercentage);
        };

        eventBus.addEventListener("Dextall.LinkedModels.ModelUpdated", onModelUpdated);

        return () => {
            eventBus.removeEventListener("Dextall.LinkedModels.ModelUpdated", onModelUpdated);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onSelectModel = () => {
        if (onSelect) {
            onSelect(model);
        }
    };

    return (
        <div
            className="linked-model-card"
            onClick={onSelectModel}
            role="button"
            tabIndex={0}
            onKeyDown={e => {
                if (e.key === "Enter" || e.key === " ") {
                    onSelectModel();
                }
            }}
        >
            <div className="linked-model-card__thumbnail">
                <LinkedModelThumbnail
                    model={model}
                    status={status}
                    translationProgressPercentage={translationProgressPercentage}
                />
            </div>

            <div className="linked-model-card__data">
                <div className="linked-model-card__title" title={model.fileName}>
                    {model.fileName}
                </div>
            </div>

            {controlsEnabled && (
                <div className="linked-model-card__controls">
                    <LinkedModelOriginEditor id={model.id} origin={model.origin} />
                    <LinkedModelVisibilityControl id={model.id} isVisible={model.isVisible} />
                    <RemoveLinkedModel id={model.id} name={model.fileName} />
                </div>
            )}
        </div>
    );
};
